import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete, AutocompleteInputChangeReason, Checkbox, Chip, DialogActions, DialogContent, DialogTitle, Grid, Icon, ListItemText, MenuItem, TextField, Typography } from "@mui/material";
import CustomDialog from "components/custom/CustomDialog";
import { CustomButton } from "components/roundedbutton/CustomButton";
import useCatchApiError from 'hooks/useCatchApiError';
import { IAbstractModel } from "model/abstract.model";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { setGuias, setOutrosVendedores } from 'redux/features/stateSlice';
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { theme } from 'theme';
import RegistraVendaService from "./registra-venda.service";

type TOutrosComissionadosProps = {
    onClose: () => void;
};

export interface FlowVendedorDTO extends IAbstractModel {
    outrosComissionados?: string[];
    formaComissao?: string | null;
    tipoAdicao?: TipoAdicaoVendedor;
    isGuia?: boolean;
}

export enum TipoAdicaoVendedor {
    REPRESENTANTE = 'REPRESENTANTE',
    OUTRO_COMISSIONADO = 'OUTRO_COMISSIONADO',
    MANUAL = 'MANUAL',
    GUIA = 'GUIA'
}

type TOutrosComissionadosInitialState = {
    vendedoresSelected: FlowVendedorDTO[];
    guiasSelected: FlowVendedorDTO[];
    vendedoresOptions: FlowVendedorDTO[];
    guiasOptions: FlowVendedorDTO[];
};

const initialState: TOutrosComissionadosInitialState = {
    vendedoresSelected: [],
    guiasSelected: [],
    vendedoresOptions: [],
    guiasOptions: [],
};

const OutrosComissionados = (props: TOutrosComissionadosProps) => {
    const [stateLocal, setStateLocal] = useState(initialState);
    const service = RegistraVendaService();
    const { vendedorPrincipal, outrosVendedores, guias } = useAppSelector(e => e.state.pedido);
    const dispatch = useAppDispatch();
    const { throwErrorMessage } = useCatchApiError();

    const getOptionsVendedores = (option: string, reason: AutocompleteInputChangeReason | null) => {
        if (reason && reason === "clear") return setStateLocal(prevState => ({ ...prevState, vendedoresSelected: [] }));

        service.getVendedoresAc(option, false)
            .then(({ data }) => {
                const result = data.filter(option => vendedorPrincipal?.uuid !== option.uuid);
                setStateLocal(prevState => ({ ...prevState, vendedoresOptions: result }));
            }).catch(error => {
                throwErrorMessage(error);
            });
    };

    const getOptionsGuias = (option: string, reason: AutocompleteInputChangeReason | null) => {
        if (reason && reason === "clear") return setStateLocal(prevState => ({ ...prevState, guiasSelected: [] }));

        service.getGuiasAc(option)
            .then(({ data }) => {
                const result = data.filter(option => vendedorPrincipal?.uuid !== option.uuid);
                setStateLocal(prevState => ({ ...prevState, guiasOptions: result }));
            }).catch(error => {
                throwErrorMessage(error);
            });
    };

    useEffect(() => {
        setStateLocal(prevState => ({ ...prevState, vendedoresSelected: outrosVendedores, guiasSelected: guias }));
        getOptionsVendedores("", null);
        getOptionsGuias("", null);
        //eslint-disable-next-line
    }, []);

    const renderOptions = (option: FlowVendedorDTO, isGuia: boolean) => {
        const optionsKey = isGuia ? 'guiasSelected' : 'vendedoresSelected';
        return (
            <MenuItem
                onClick={() =>
                    setStateLocal(prev => {
                        const estaSelecionado = !!prev[optionsKey].find(s => s.uuid === option.uuid);
                        const optionsSelected = estaSelecionado
                            ? prev[optionsKey].filter(s => s.uuid !== option.uuid)
                            : [...prev[optionsKey], { ...option, }];
                        return { ...prev, [optionsKey]: optionsSelected };
                    })
                }
                key={option.uuid}
                value={option.descritivo}>
                <Checkbox
                    id={`item-${option.descritivo?.toLowerCase()}-input`}
                    sx={{
                        '&.MuiCheckbox-root.Mui-checked': {
                            color: theme.palette.primary.main
                        }
                    }}
                    checked={stateLocal[optionsKey].some(e => e.uuid === option.uuid)}
                />
                <ListItemText primary={option.descritivo} />
            </MenuItem>
        );
    };

    const onConfirm = useCallback((e: FormEvent) => {
        e.preventDefault();
        dispatch(setOutrosVendedores(stateLocal.vendedoresSelected));
        dispatch(setGuias(stateLocal.guiasSelected));
        props.onClose();
        //eslint-disable-next-line
    }, [stateLocal.vendedoresSelected, stateLocal.guiasSelected]);

    const onRemoveChip = useCallback((uuid: string, isGuia: boolean) => {
        const optionsKey = isGuia ? 'guiasSelected' : 'vendedoresSelected';
        setStateLocal(prevState => ({ ...prevState, [optionsKey]: prevState[optionsKey].filter(e => e.uuid !== uuid) }));
        //eslint-disable-next-line
    }, []);

    return (
        <CustomDialog
            id="cadastro-pessoa-dialog"
            open={true}
            fullWidth={true}
            maxWidth={'lg'}
            onClose={props.onClose}
        >
            <form onSubmit={onConfirm} style={{ gap: 0 }}>
                <DialogTitle id="dialog-title" textAlign={'center'} sx={{ pb: '0 !important' }}>
                    <Typography fontSize={30}>Outros comissionados</Typography>
                </DialogTitle>

                <DialogContent id="dialog-content" sx={{ display: 'flex', flexDirection: 'column', gap: '30px', pt: '15px !important', alignItems: 'center', maxHeight: "500px" }}>
                    <Grid container spacing={2}>
                        <Autocomplete
                            sx={{ overflowX: 'hidden', overflowY: 'auto', width: '100%' }}
                            multiple
                            value={stateLocal.vendedoresSelected}
                            options={stateLocal.vendedoresOptions}
                            getOptionLabel={(option) => option.descritivo}
                            onInputChange={(_, value, reason) => getOptionsVendedores(value, reason)}
                            onChange={(_, value) => setStateLocal(prev => ({ ...prev, vendedoresSelected: value }))}
                            noOptionsText="Nenhum valor encontrado"
                            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="standard"
                                    label="Vendedores"
                                />)}
                            disableClearable
                            renderOption={(_, option) => renderOptions(option, false)}
                            renderTags={(p) => p.map((e, i) => {
                                return (
                                    <Chip
                                        key={e.uuid}
                                        sx={{
                                            color: theme.palette.background.default,
                                            background: theme.palette.primary.main,
                                            marginRight: '3px',
                                        }}
                                        onDelete={() => onRemoveChip(e.uuid, false)}
                                        label={e.descritivo.split(" - ")[1] ?? ''}
                                        deleteIcon={<CloseIcon sx={{ color: `${theme.palette.background.default} !important` }} />}
                                        size='small'
                                    />
                                );
                            })}
                        />
                    </Grid>

                    <Grid container spacing={2}>
                        <Autocomplete
                            sx={{ overflowX: 'hidden', overflowY: 'auto', width: '100%' }}
                            multiple
                            value={stateLocal.guiasSelected}
                            options={stateLocal.guiasOptions}
                            getOptionLabel={(option) => option.descritivo}
                            onInputChange={(_, value, reason) => getOptionsVendedores(value, reason)}
                            onChange={(_, value) => setStateLocal(prev => ({ ...prev, guiasSelected: value }))}
                            noOptionsText="Nenhum valor encontrado"
                            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="standard"
                                    label="Guias"
                                />)}
                            disableClearable
                            renderOption={(_, option) => renderOptions(option, true)}
                            renderTags={(p) => p.map((e, i) => {
                                return (
                                    <Chip
                                        key={e.uuid}
                                        sx={{
                                            color: theme.palette.background.default,
                                            background: theme.palette.primary.main,
                                            marginRight: '3px',
                                        }}
                                        onDelete={() => onRemoveChip(e.uuid, true)}
                                        label={e.descritivo.split(" - ")[1] ?? ''}
                                        deleteIcon={<CloseIcon sx={{ color: `${theme.palette.background.default} !important` }} />}
                                        size='small'
                                    />
                                );
                            })}
                        />
                    </Grid>
                </DialogContent >

                <DialogActions
                    sx={{
                        justifyContent: 'space-between'
                    }}>
                    <CustomButton onClick={props.onClose} variant="contained" color="error" startIcon={<Icon className='fa-solid fa-xmark' />}>
                        Cancelar
                    </CustomButton>
                    <CustomButton type="submit" disabled={false} variant="contained" color="success" startIcon={<Icon className='fa-solid fa-check' />}>
                        Confirmar
                    </CustomButton>
                </DialogActions>
            </form >
        </CustomDialog >
    );
};

export default OutrosComissionados;
